/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Row, Col, Layout, Input, Select, DatePicker, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { omit } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import queryString from 'query-string';

import { TableList } from 'components/table-list';
import PdButton from 'components/pd-button';
import OrderPickupService from 'services/order-pickup';
import PickUpPointService from 'services/pickup-point';
import { errHandling, convertNumberToRupiahCurrency } from 'utils';

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { Text } = Typography;

const SORT_ORDER_LIST = [
  { value: '', label: 'Sort By' },
  { value: 'ordered_at asc', label: 'Oldest' },
  { value: 'ordered_at desc', label: 'Latest' },
];
const dateFormat = 'YYYY/MM/DD';

export function InboundOrOutboundOrdersList({ pageTitle }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [endDatePick, setEndDatePick] = useState('');
  const [nextPage, setNextPage] = useState(-1);
  const [orderPickUpList, setOrderPickUpList] = useState([]);
  const [pickupPoint, setPickupPoint] = useState('');
  const [pickupPointList, setPickupPointList] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [startDatePick, setStartDatePick] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const parsed = queryString.parse(location.search);

  async function getOrderPickUpList({
    cursor = parsed.page || currentPage,
    end_date = parsed.end_date,
    limit = 10,
    pickup_point_id = parsed.pickup_point_id,
    q = parsed.q,
    sort_by = parsed.sort_by,
    start_date = parsed.start_date,
  } = {}) {
    try {
      const { data: { data, metadata: { next_cursor } = {} } = {} } =
        await OrderPickupService.getOrderList({
          cursor,
          end_date,
          limit,
          pickup_point_id,
          q,
          sort_by,
          start_date,
          statuses: [
            location.pathname.includes('inbound')
              ? 'sent_to_pickup'
              : 'arrived_at_pickup',
          ],
        });

      setOrderPickUpList(data);
      setNextPage(next_cursor);
      if (parsed.page) setCurrentPage(parsed.page);
    } catch (error) {
      errHandling(error);
    }
  }

  async function fetchPickupPointList() {
    try {
      const {
        data: { data },
      } = await PickUpPointService.getPickUpPointList({
        status: 'active',
      });

      setPickupPointList(data);
    } catch (error) {
      errHandling(error);
    }
  }

  function handleOnClickDetails(id) {
    navigate(`${location.pathname}/detail?id=${id}`);
  }

  const columns = [
    {
      name: 'Package ID.',
      selector: row => row.format_package_id,
      width: "160px"
    },
    {
      name: 'Date',
      selector: row => row.ordered_at,
      center: true,
    },
    {
      name: 'Customer',
      selector: row => row.user.full_name,
      center: true,
    },
    {
      name: 'Warehouse Loc',
      selector: row => row.warehouse.title,
      center: true,
    },
    {
      name: 'Pick Up Point',
      selector: row => row.pickup_point.title,
      cell: (row) => (
      <div
        style={{
          width: "100%",
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
          }}
        >
          {row.pickup_point.title}
        </div>
      </div>)
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      center: true,
      format: (row) => convertNumberToRupiahCurrency(row.net_price),
    },
    {
      center: true,
      width: '15%',
      cell: DetailButton,
    },
  ];

  function DetailButton({ id }) {
    return (
      <PdButton
        className="height-32"
        onClick={handleOnClickDetails.bind(this, id)}
        type="primary">
        Details
      </PdButton>
    );
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed.page = page;
    const stringified = queryString.stringify(parsed);
    navigate(`${location.pathname}?${stringified}`);
  }

  function handleOnChangeSearch({ target: { value } } = {}) {
    setSearch(value);
  }

  function handleOnChangeDate(_, dateString = []) {
    const [startDate, endDate] = dateString;
    if (startDate === '') {
      delete parsed.start_date;
    } else {
      parsed.start_date = startDate;
    }

    if (endDate === '') {
      delete parsed.end_date;
    } else {
      parsed.end_date = endDate;
    }

    parsed.page = 1;
    setStartDatePick(startDate);
    setEndDatePick(endDate);
    const stringified = queryString.stringify(parsed);
    navigate(`${location.pathname}?${stringified}`);
  }

  function filterHandler(params, value) {
    if (!value) {
      delete parsed[params];
    } else {
      parsed[params] = value;
    }

    const actions = {
      pickup_point_id: setPickupPoint,
      q: setSearch,
      sort_by: setSortBy,
    };

    actions[params](value);

    parsed.page = 1;
    const stringified = queryString.stringify(parsed);
    navigate(`${location.pathname}?${stringified}`);
  }

  function resetFilter() {
    setSearch('');
    setPickupPoint('');
    setCurrentPage(1);
    setSortBy('');
    setStartDatePick('');
    setEndDatePick('');
    const stringified = queryString.stringify(
      omit(parsed, [
        'end_date',
        'page',
        'pickup_point_id',
        'q',
        'sort_by',
        'start_date',
      ])
    );
    navigate(`${location.pathname}?${stringified}`);
  }

  useEffect(() => {
    getOrderPickUpList();
    // eslint-disable-next-line
  }, [
    parsed.end_date,
    parsed.page,
    parsed.pickup_point_id,
    parsed.q,
    parsed.sort_by,
    parsed.start_date,
  ]);

  useEffect(() => {
    fetchPickupPointList();
  }, []);

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">{pageTitle}</div>
        <Content className="pageContent">
          <Row
            align="middle"
            className="pd-margin-bottom-md"
            gutter={18}
            type="flex">
            <Col span={18}>
              <Search
                className="search-product"
                enterButton
                onChange={handleOnChangeSearch}
                placeholder="Customer name"
                value={search}
                onSearch={filterHandler.bind(this, 'q')}
              />
            </Col>
          </Row>
          <Row gutter={[18, 36]}>
            <Col span={6}>
              <RangePicker
                className="w-full"
                format={dateFormat}
                onChange={handleOnChangeDate}
                value={[
                  startDatePick && dayjs(startDatePick, dateFormat),
                  endDatePick && dayjs(endDatePick, dateFormat),
                ]}
              />
            </Col>
            <Col span={4}>
              <Select
                className="w-full"
                onChange={filterHandler.bind(this, 'pickup_point_id')}
                placeholder="Pickup Point"
                value={pickupPoint}>
                {[{ id: '' }, ...pickupPointList].map(({ id, title } = {}) => (
                  <Option hidden={!title} key={id} value={id}>
                    {!title ? 'Pickup Point' : title}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={3}>
              <Select
                className="w-full"
                onChange={filterHandler.bind(this, 'sort_by')}
                value={sortBy}>
                {SORT_ORDER_LIST.map(({ value, label } = {}, idx) => (
                  <Option hidden={!value} key={idx} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Text className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </Text>
            </Col>
          </Row>

          <Row>
            <TableList
              columns={columns}
              changePage={changePage}
              nextPage={nextPage}
              orderList={orderPickUpList}
              page={currentPage}
              responsive={false}
            />
          </Row>
        </Content>
      </div>
    </Layout>
  );
}
